<script setup lang="ts">
import BaseLoader from '@/components/BaseComponents/BaseLoader.vue';
import PlanCard from '@/components/Plan/PlanCard.vue';
import { useExternalUrl } from '@/composables/useExternalUrl';
import { useFetch } from '@/composables/useFetch';
import { useInAppPurchase } from '@/composables/useInAppPurchase';
import { usePlatform } from '@/composables/usePlatform';
import { useProfileRestaurant } from '@/composables/useProfileRestaurant';
import { useSubscription } from '@/composables/useSubscription';
import { useAppStore } from '@/stores/appStore';
import { useEnvStore } from '@/stores/envStore';
import { useUserStore } from '@/stores/useUserStore';
import type { SubscriptionPlan } from '@/types/api';
import type { Restaurant } from '@/types/restaurants';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputSwitch from 'primevue/inputswitch';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { fetchData } = useFetch();
const toast = useToast();
const { t } = useI18n();
const { navigateToExternalUrl } = useExternalUrl();
const { isNative, isIOS } = usePlatform();
const {
  initializeStore,
  purchaseSubscription,
  redeemOfferCode,
  openManageSubscriptions,
  isProcessing: isIAPProcessing,
  isPlanAvailableForPurchase,
  isInitialized: isIAPInitialized,
} = useInAppPurchase();

const appStore = useAppStore();
const envStore = useEnvStore();
const route = useRoute();
const router = useRouter();
const planId = route.params.planId as string;
const plan = ref<SubscriptionPlan | null>(null);
const email = ref('');
const userStore = useUserStore();
const restaurantId = ref(userStore.activeRestaurant?.id);

// Coupon code functionality
const couponCode = ref('');
const showCouponInput = ref(false);
const isCouponValidating = ref(false);
const couponValidation = ref<{
  isValid: boolean;
  coupon?: {
    id: string;
    name: string;
    percent_off?: number;
    amount_off?: number;
    currency?: string;
  };
  error?: string;
} | null>(null);

// Add comprehensive loading states
const isInitialLoading = ref(true);
const isSubmitting = ref(false);
const isSavingAddress = ref(false);

const { employeeCount, adminCount, fetchCurrentPlan, canDowngradeToPlan, refreshTeamData, loading: subscriptionLoading } = useSubscription();

const initialBillingPeriod = route.query.billing_period as string;
const billingPeriod = ref(initialBillingPeriod || 'yearly');

// Computed to check if any loading is happening
const isAnyLoading = computed(
  () =>
    isInitialLoading.value ||
    subscriptionLoading.value ||
    isSubmitting.value ||
    isSavingAddress.value ||
    isCouponValidating.value ||
    isIAPProcessing.value,
);

const isYearly = computed({
  get: () => billingPeriod.value === 'yearly',
  set: (value) => {
    if (isAnyLoading.value) return; // Prevent changes during loading
    billingPeriod.value = value ? 'yearly' : 'monthly';
  },
});

const supportsBothBillingPeriods = computed(() => {
  if (!plan.value?.price_monthly || !plan.value.price_yearly) return false;
  const monthlyPrice = Number(plan.value.price_monthly);
  const yearlyPrice = Number(plan.value.price_yearly);
  return monthlyPrice > 0 && yearlyPrice > 0;
});

// Check if this is a free plan (no payment required)
const isFreePlan = computed(() => {
  if (!plan.value) return false;
  const monthlyPrice = Number(plan.value.price_monthly || 0);
  const yearlyPrice = Number(plan.value.price_yearly || 0);
  return monthlyPrice === 0 && yearlyPrice === 0;
});

// Check if this is a valid downgrade
const downgradeValidation = computed(() => {
  if (!plan.value) return { canDowngrade: true, reasons: [], impact: {} };
  return canDowngradeToPlan(plan.value);
});

const currentPlan = computed(() => userStore.user?.active_restaurant?.subscription?.plan || null);

// Check if user has an Apple subscription (cannot change subscription on native iOS app)
const hasAppleSubscription = computed(() => {
  return userStore.user?.active_restaurant?.subscription?.platform === 'apple';
});

// Check if subscription change should be blocked (native iOS with Apple subscription)
const isSubscriptionChangeBlocked = computed(() => {
  return isNative.value && isIOS.value && hasAppleSubscription.value && isChangingSubscription.value;
});

// Check if the current billing period is available for purchase on iOS
const isBillingPeriodUnavailable = computed(() => {
  if (!isNative.value || !isIOS.value || !plan.value || !isIAPInitialized.value) {
    return false;
  }
  const currentBillingPeriod = billingPeriod.value as 'monthly' | 'yearly';
  return !isPlanAvailableForPurchase(plan.value, currentBillingPeriod);
});

// Add this computed property after the existing downgradeValidation
const isDowngradingFromBusiness = computed(() => {
  if (!plan.value || !isChangingSubscription.value) return false;

  if (!currentPlan.value) return false;

  return currentPlan.value.category === 'business' && plan.value.category === 'private';
});

watch(billingPeriod, (newPeriod) => {
  if (!isAnyLoading.value) {
    const newQuery = { ...route.query, billing_period: newPeriod };
    router.replace({ query: newQuery });
  }
});

const { postRestaurantAddress } = useProfileRestaurant(toast, restaurantId.value!);

const addressLine1 = ref('');
const city = ref('');
const postalCode = ref();
const restaurant = ref<Restaurant | null>(null);

const isAddressComplete = computed(() => {
  return !!addressLine1.value && !!postalCode.value && !!city.value;
});

const isChangingSubscription = computed(
  () => userStore.user?.active_restaurant?.subscription !== null && userStore.user?.active_restaurant?.subscription?.platform !== 'trial',
);

const showAddressDialog = ref(false);
const tempAddressLine1 = ref('');
const tempAddressCity = ref('');
const tempAddressPostalCode = ref('');

function toggleCouponInput() {
  if (isAnyLoading.value) return;
  showCouponInput.value = !showCouponInput.value;
  if (!showCouponInput.value) {
    couponCode.value = '';
    couponValidation.value = null;
  }
}

// Coupon validation function
const validateCouponCode = async (code: string) => {
  if (!code.trim()) {
    couponValidation.value = null;
    return;
  }

  isCouponValidating.value = true;

  try {
    const { data, error, statusCode } = await fetchData(`${envStore.apiUrl}/subscription/validate-coupon`, 'POST', {
      coupon_code: code.trim(),
      plan_id: plan.value?.id,
      billing_period: billingPeriod.value,
    });

    if (statusCode === 200 && data?.valid) {
      couponValidation.value = {
        isValid: true,
        coupon: data.coupon,
      };
    } else if (error || !data?.valid) {
      couponValidation.value = {
        isValid: false,
        error: error?.details.error || t('subscription.confirmSubscription.couponCodeError'),
      };
    }
  } catch (err) {
    console.error('Error validating coupon:', err);
    couponValidation.value = {
      isValid: false,
      error: t('subscription.confirmSubscription.couponCodeError'),
    };
  } finally {
    isCouponValidating.value = false;
  }
};

function openAddressDialog() {
  if (isAnyLoading.value) return;

  tempAddressLine1.value = addressLine1.value;
  tempAddressCity.value = city.value;
  tempAddressPostalCode.value = postalCode.value;
  showAddressDialog.value = true;
}

async function validateAddressDialog() {
  if (isSavingAddress.value) return;

  isSavingAddress.value = true;

  try {
    const updatedRestaurant = await postRestaurantAddress({
      address_line_1: tempAddressLine1.value,
      city: tempAddressCity.value,
      postal_code: tempAddressPostalCode.value,
    });

    if (updatedRestaurant) {
      addressLine1.value = tempAddressLine1.value;
      city.value = tempAddressCity.value;
      postalCode.value = tempAddressPostalCode.value;
      showAddressDialog.value = false;
    }
  } finally {
    isSavingAddress.value = false;
  }
}

function cancelAddressDialog() {
  if (isSavingAddress.value) return;
  showAddressDialog.value = false;
}

const fetchRestaurant = async () => {
  const restaurantId = userStore.activeRestaurant?.id;
  if (!restaurantId) return;
  const { data } = await fetchData(`${envStore.apiUrl}/restaurants/${restaurantId}`, 'GET');
  if (data) {
    restaurant.value = data as Restaurant;
    addressLine1.value = restaurant.value.address?.address_line_1 ?? '';
    city.value = restaurant.value.address?.city?.name ?? '';
    postalCode.value = restaurant.value.address?.city?.postal_code ?? '';
  }
};

const fetchPlan = async () => {
  const { data } = await fetchData(`${envStore.apiUrl}/plans/${planId}`);
  if (data) {
    plan.value = data as SubscriptionPlan;
  } else {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Unable to fetch plan details.',
      life: 3000,
    });
  }
};

const isValidEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const showForceDowngradeDialog = ref(false);

// Store backend downgrade validation when available
const backendDowngradeValidation = ref<{
  canDowngrade: boolean;
  reasons: string[];
  impact: { employees_to_remove?: number; admins_to_convert?: number };
} | null>(null);

// Use backend validation if available, otherwise fall back to frontend validation
const effectiveDowngradeValidation = computed(() => {
  return backendDowngradeValidation.value || downgradeValidation.value;
});

// Add computed for the dialog message
// const forceDowngradeMessage = computed(() => {
//   if (!downgradeValidation.value.impact) return '';

//   const { employees_to_remove, admins_to_convert } = downgradeValidation.value.impact;
//   let message = t('subscription.downgrade.forceDowngrade.confirmMessage');

//   if (employees_to_remove && employees_to_remove > 0) {
//     message += '\n\n' + t('subscription.downgrade.forceDowngrade.employeesWillBeRemoved', { count: employees_to_remove });
//   }

//   if (admins_to_convert && admins_to_convert > 0) {
//     message += '\n\n' + t('subscription.downgrade.forceDowngrade.adminsWillBeRemoved', { count: admins_to_convert });
//   }

//   return message;
// });

const handleChangeSubscription = async (forceDowngrade = false) => {
  if (!plan.value) return;

  try {
    const payload = {
      new_plan_id: plan.value.id,
      email: email.value || undefined,
      billing_period: billingPeriod.value,
      force_downgrade: forceDowngrade,
      coupon_code: couponCode.value || undefined,
    };
    const { data, error, statusCode } = await fetchData(`${envStore.apiUrl}/subscription/change`, 'POST', payload);

    console.log('Change subscription response:', { data, error, statusCode });
    if (statusCode === 402 && data?.payment_intent_client_secret) {
      toast.add({
        severity: 'warn',
        summary: t('subscription.paymentActionRequired.title'),
        detail: t('subscription.paymentActionRequired.detail'),
        life: 5000,
      });
    } else if (data?.is_checkout && data?.url) {
      if (isNative.value) {
        await navigateToExternalUrl(data.url, { target: '_blank' });
      } else {
        await navigateToExternalUrl(data.url);
      }
    } else if (error && statusCode === 400 && error.details?.downgrade_impact && !forceDowngrade) {
      // When we get a downgrade error from the backend, refresh team data to ensure validation is current
      await refreshTeamData();

      // Store the backend validation result to show accurate impact
      backendDowngradeValidation.value = {
        canDowngrade: false,
        reasons: Array.isArray(data?.errors) ? data.errors : [],
        impact: error.details.downgrade_impact || {},
      };

      // Show force downgrade dialog with the real backend data
      showForceDowngradeDialog.value = true;
    } else if (error && statusCode === 400 && data?.error) {
      // Handle specific coupon code errors
      const errorMessage = data.error;
      let toastDetail = '';

      if (errorMessage.includes('coupon_invalid')) {
        toastDetail = t('subscription.confirmSubscription.couponCodeInvalid');
      } else if (errorMessage.includes('coupon_expired')) {
        toastDetail = t('subscription.confirmSubscription.couponCodeInvalid'); // Use same message for UX
      } else if (errorMessage.includes('coupon_error')) {
        toastDetail = t('subscription.confirmSubscription.couponCodeError');
      } else {
        toastDetail = data.message || t('subscription.errorChange');
      }

      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: toastDetail,
        life: 5000,
      });
    } else if (error) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: data?.message || t('subscription.errorChange'),
        life: 3000,
      });
    } else if (data && data.message) {
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: data.message || t('subscription.successfullyChanged'),
        life: 3000,
      });
      // Clear backend validation on success
      backendDowngradeValidation.value = null;
      await userStore.refreshUser();
      router.go(-1);
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : t('subscription.errorChange');
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: message,
      life: 3000,
    });
  }
};

const confirmAction = async () => {
  if (!plan.value || isSubmitting.value) return;

  // For free plans, skip all payment-related validation and go directly to subscription creation
  if (isFreePlan.value) {
    isSubmitting.value = true;
    try {
      await handleChangeSubscription();
    } finally {
      isSubmitting.value = false;
    }
    return;
  }

  // For iOS native app, use In-App Purchase (only for paid plans)
  if (isNative.value && isIOS.value && !isChangingSubscription.value) {
    isSubmitting.value = true;
    try {
      const success = await purchaseSubscription(plan.value, billingPeriod.value as 'monthly' | 'yearly');
      if (success) {
        // Purchase flow will handle the rest via callbacks
        return;
      }
    } catch (error) {
      console.error('IAP purchase error:', error);
    } finally {
      isSubmitting.value = false;
    }
    return;
  }

  // For web or subscription changes, use Stripe
  // For new subscriptions (paid plans), validate address and email
  if (!isChangingSubscription.value) {
    if (!isAddressComplete.value) {
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: t('subscription.confirmSubscription.addressMissing'),
        life: 3000,
      });
      return;
    }
    if (!isValidEmail(email.value)) {
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: t('auth.user.form.error.invalid_email'),
        life: 3000,
      });
      return;
    }
  }

  isSubmitting.value = true;

  try {
    await handleChangeSubscription();
  } finally {
    isSubmitting.value = false;
  }
};

const confirmForceDowngrade = async () => {
  if (isSubmitting.value) return;

  isSubmitting.value = true;
  showForceDowngradeDialog.value = false;

  try {
    await handleChangeSubscription(true);
  } finally {
    isSubmitting.value = false;
  }
};

const cancelForceDowngrade = () => {
  showForceDowngradeDialog.value = false;
  backendDowngradeValidation.value = null;
};

// Update the isConfirmDisabled computed - now only disable for loading and address/email validation
const isConfirmDisabled = computed(() => {
  // Disable if any loading is happening
  if (isAnyLoading.value) return true;

  // Disable if subscription change is blocked (native iOS with Apple subscription)
  if (isSubscriptionChangeBlocked.value) return true;

  // Disable if billing period is not available on iOS
  if (isBillingPeriodUnavailable.value) return true;

  // Free plans don't require address or email validation
  if (isFreePlan.value) return false;

  // For new subscriptions (paid plans), require address completion
  if (!isChangingSubscription.value && !isAddressComplete.value) return true;

  return false;
});

const initializeData = async () => {
  isInitialLoading.value = true;

  try {
    // Initialize IAP store for iOS native
    if (isNative.value && isIOS.value) {
      await initializeStore();
    }

    // Fetch plan and restaurant data
    await Promise.all([fetchPlan(), fetchRestaurant()]);

    // Always refresh team data to ensure validation is based on current team composition
    await Promise.all([refreshTeamData(), fetchCurrentPlan()]);

    // Set initial email
    restaurantId.value = userStore.activeRestaurant?.id;
    if (userStore.activeRestaurant?.billing_email) {
      email.value = userStore.activeRestaurant.billing_email;
    } else if (userStore.user?.email) {
      email.value = userStore.user.email;
    }
  } catch (error) {
    console.error('Error initializing confirm plan view:', error);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('subscription.errorInitializing'),
      life: 3000,
    });
  } finally {
    isInitialLoading.value = false;
  }
};

onMounted(initializeData);
</script>

<template>
  <!-- iOS Message with Magic Link -->
  <!-- <div v-if="isNative" class="mx-auto flex max-w-2xl flex-col gap-6 p-6">
    <div class="rounded-lg border border-blue-200 bg-blue-50 p-6 text-center">
      <i class="pi pi-info-circle mb-3 text-4xl text-blue-600"></i>
      <h3 class="mb-2 font-semibold text-blue-900">{{ t('subscription.iosManageTitle') }}</h3>
      <p class="mb-4 text-blue-800">{{ t('subscription.iosManageMessage') }}</p>
      <Button
        :label="t('subscription.magicLink.generate')"
        icon="pi pi-link"
        severity="info"
        :loading="isGeneratingMagicLink"
        @click="
          () =>
            generateAndOpenMagicLink(`/subscription/plans/${route.params.planId}/confirm?billing_period=${route.query.billing_period || 'yearly'}`)
        "
      />
    </div>
  </div> -->

  <!-- Loading overlay for initial data fetch -->
  <div v-if="isInitialLoading" class="flex min-h-[400px] items-center justify-center">
    <BaseLoader />
  </div>

  <div v-else class="mx-auto mt-8 flex max-w-md flex-col items-center gap-4">
    <!-- Apple Subscription Change Blocked Message -->
    <div v-if="isSubscriptionChangeBlocked" class="w-full rounded-lg border border-red-200 bg-red-50 p-6 text-center">
      <i class="pi pi-apple mb-3 text-4xl text-red-600"></i>
      <h3 class="mb-2 font-semibold text-red-900">{{ t('subscription.iap.cannotChangeSubscription') }}</h3>
      <p class="mb-4 text-red-800">{{ t('subscription.iap.manageAppleSubscription') }}</p>
      <Button
        :label="t('subscription.iap.openAppStoreSettings')"
        icon="pi pi-external-link"
        severity="danger"
        outlined
        @click="openManageSubscriptions()"
      />
    </div>

    <!-- iOS App Store pricing warning (only show if not blocked) -->
    <!-- <div v-else-if="isNative && isIOS" class="w-full rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div class="flex items-start gap-3">
        <i class="pi pi-info-circle mt-0.5 text-lg text-amber-600"></i>
        <div>
          <p class="font-medium text-amber-900">{{ t('subscription.iap.applePricingWarningShort') }}</p>
          <p class="mt-1 text-sm text-amber-800">{{ t('subscription.iap.applePricingWarning') }}</p>
        </div>
      </div>
    </div> -->

    <!-- Downgrade Warning -->
    <Message v-if="!effectiveDowngradeValidation.canDowngrade" severity="warn" :closable="false" class="w-full">
      <div class="flex flex-col gap-2">
        <span class="font-semibold">{{ t('subscription.downgrade.teamAdjustmentRequired') }}</span>
        <ul class="text-sm">
          <li v-for="reason in effectiveDowngradeValidation.reasons" :key="reason" class="flex items-start gap-2">
            <!-- <i class="pi pi-exclamation-triangle mt-0.5 flex-shrink-0"></i> -->
            <span>{{ reason }}</span>
          </li>
        </ul>
        <p class="mt-2 text-sm">{{ t('subscription.downgrade.willBeAdjustedAutomatically') }}</p>
      </div>
    </Message>

    <!-- Business to Private Downgrade Warning -->
    <Message v-if="isDowngradingFromBusiness" severity="warn" :closable="false" class="w-full">
      <div class="flex flex-col gap-2">
        <span class="font-semibold">{{ t('subscription.downgrade.businessToPrivateWarning.title') }}</span>
        <p class="text-sm">{{ t('subscription.downgrade.businessToPrivateWarning.message') }}</p>
        <ul class="text-sm">
          <li class="flex items-start gap-2">
            <i class="pi pi-minus-circle mt-0.5 flex-shrink-0"></i>
            <span>{{ t('subscription.downgrade.businessToPrivateWarning.loseTeamManagement') }}</span>
          </li>
          <li class="flex items-start gap-2">
            <i class="pi pi-minus-circle mt-0.5 flex-shrink-0"></i>
            <span>{{ t('subscription.downgrade.businessToPrivateWarning.loseAdvancedFeatures') }}</span>
          </li>
          <!-- <li class="flex items-start gap-2">
            <i class="pi pi-minus-circle mt-0.5 flex-shrink-0"></i>
            <span>{{ t('subscription.downgrade.businessToPrivateWarning.loseReporting') }}</span>
          </li> -->
        </ul>
      </div>
    </Message>

    <!-- Billing Period Toggle -->
    <div v-if="supportsBothBillingPeriods" class="w-full">
      <div class="flex flex-col items-center">
        <div class="mb-2 flex items-center justify-center gap-4 rounded-lg bg-gray-50 p-4">
          <span
            class="text-sm text-primary-900 transition-colors"
            :class="{
              'font-semibold text-primary-600': !isYearly,
              'cursor-pointer': !isAnyLoading,
              'cursor-not-allowed opacity-50': isAnyLoading,
            }"
            @click="!isAnyLoading && (isYearly = false)"
          >
            {{ t('subscription.monthly') }}
          </span>
          <InputSwitch v-model="isYearly" input-id="billing-period-switch" :disabled="isAnyLoading" class="mx-2" />
          <span
            class="text-sm text-primary-900 transition-colors"
            :class="{
              'font-semibold text-primary-600': isYearly,
              'cursor-pointer': !isAnyLoading,
              'cursor-not-allowed opacity-50': isAnyLoading,
            }"
            @click="!isAnyLoading && (isYearly = true)"
          >
            {{ t('subscription.yearly') }}
          </span>
        </div>
        <span v-if="plan?.price_monthly && plan?.price_yearly" class="ml-2 rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
          {{ t('subscription.saveWithYearly') }}
        </span>
      </div>
    </div>

    <!-- Billing Period Unavailable Warning (iOS) -->
    <Message v-if="isBillingPeriodUnavailable" severity="warn" :closable="false" class="w-full">
      <div class="flex items-center gap-2">
        <i class="pi pi-apple"></i>
        <span>{{
          t('subscription.iap.billingPeriodUnavailable', {
            period: isYearly ? t('subscription.yearly').toLowerCase() : t('subscription.monthly').toLowerCase(),
          })
        }}</span>
      </div>
    </Message>

    <!-- Plan Card -->
    <PlanCard v-if="plan" :plan="plan" :selected="true" :show-features="true" :is-yearly="isYearly" />

    <!-- Free plan info message -->
    <Message v-if="isFreePlan && !isChangingSubscription" severity="info" :closable="false" class="w-full">
      <div class="flex items-center gap-2">
        <i class="pi pi-check-circle"></i>
        <span>{{ t('subscription.freePlan.noPaymentRequired') }}</span>
      </div>
    </Message>

    <!-- Current team info for context -->
    <div
      v-if="
        isChangingSubscription && (employeeCount > 0 || adminCount > 1) && (currentPlan?.category === 'business' || plan?.category === 'business')
      "
      class="w-full rounded-lg bg-blue-50 p-4"
    >
      <h4 class="mb-2 font-semibold text-blue-800">{{ t('subscription.currentTeam') }}</h4>
      <div class="text-sm text-blue-700">
        <p>{{ t('subscription.employees') }}: {{ employeeCount }}</p>
        <p>{{ t('subscription.admins') }}: {{ adminCount }}</p>
      </div>
    </div>

    <!-- Email and address fields for new subscriptions (paid plans only) -->
    <div v-if="!isChangingSubscription && !isFreePlan" class="items-left flex w-full flex-col gap-2">
      <label class="font-semibold" for="subscription-email">
        {{ t('subscription.confirmSubscription.emailLabel') }}
      </label>
      <InputText id="subscription-email" v-model="email" type="email" class="w-full" autocomplete="email" :disabled="isAnyLoading" />
    </div>

    <div v-if="!isChangingSubscription && !isFreePlan" class="w-full">
      <p class="font-semibold">
        {{ t('profile.profile.restaurantTab.historyAndData.addressPlaceholder') }}
      </p>
      <div class="flex flex-row items-center justify-between">
        <div v-if="isAddressComplete">
          <p>{{ addressLine1 }}</p>
          <p>{{ postalCode }}, {{ city }}</p>
        </div>
        <div v-else class="text-sm italic">
          {{ t('subscription.confirmSubscription.addressMissing') }}
        </div>
        <div>
          <Button class="ml-4" icon="pi pi-pencil" :disabled="isAnyLoading" @click="openAddressDialog" />
        </div>
      </div>
    </div>

    <!-- Coupon code section for new subscriptions only (paid plans) -->
    <div v-if="!isChangingSubscription && !isFreePlan && !(isNative && isIOS)" class="w-full">
      <div class="flex items-center justify-between">
        <span class="font-semibold">{{ t('subscription.confirmSubscription.couponCodeLabel') }}</span>
        <Button
          :label="showCouponInput ? t('common.cancel') : t('subscription.confirmSubscription.applyCouponCode')"
          :icon="showCouponInput ? 'pi pi-times' : 'pi pi-tag'"
          text
          size="small"
          :disabled="isAnyLoading"
          @click="toggleCouponInput"
        />
      </div>
      <div v-if="showCouponInput" class="mt-2 space-y-2">
        <div class="relative">
          <div class="flex flex-row items-center gap-3">
            <InputText
              v-model="couponCode"
              :placeholder="t('subscription.confirmSubscription.couponCodePlaceholder')"
              class="w-full"
              :disabled="isAnyLoading"
              :class="{
                'border-green-500': couponValidation?.isValid,
                'border-red-500': couponValidation && !couponValidation?.isValid,
              }"
            />
            <Button
              :label="t('common.validate')"
              :loading="isCouponValidating"
              :disabled="isCouponValidating"
              size="small"
              class="!px-4"
              @click="validateCouponCode(couponCode)"
            />
          </div>
          <!-- Loading spinner inside input -->
          <div v-if="isCouponValidating" class="absolute right-3 top-1/2 -translate-y-1/2 transform">
            <i class="pi pi-spin pi-spinner text-gray-400"></i>
          </div>
        </div>

        <!-- Coupon validation feedback -->
        <div v-if="couponValidation?.isValid && couponValidation?.coupon" class="rounded-lg border border-green-200 bg-green-50 p-3">
          <div class="flex items-center gap-2 text-green-800">
            <i class="pi pi-check-circle"></i>
            <span class="font-medium">{{ t('subscription.confirmSubscription.couponCodeValid') }}</span>
          </div>
          <div class="mt-1 text-sm text-green-700">
            <span v-if="couponValidation?.coupon?.percent_off">
              {{
                t('subscription.confirmSubscription.discountPercent', {
                  percent: couponValidation?.coupon?.percent_off,
                })
              }}
            </span>
            <span v-else-if="couponValidation?.coupon?.amount_off">
              {{
                t('subscription.confirmSubscription.discountAmount', {
                  amount: couponValidation?.coupon?.amount_off,
                  currency: couponValidation?.coupon?.currency?.toUpperCase(),
                })
              }}
            </span>
          </div>
        </div>

        <!-- Error message -->
        <div v-else-if="couponValidation && !couponValidation?.isValid" class="rounded-lg border border-red-200 bg-red-50 p-3">
          <div class="flex items-center gap-2 text-red-800">
            <i class="pi pi-exclamation-triangle"></i>
            <span class="text-sm">{{ couponValidation?.error }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Apple Offer Code Redemption (iOS native only) -->
    <div v-if="isNative && isIOS && !isChangingSubscription && !isFreePlan" class="w-full">
      <div class="flex items-center justify-between">
        <span class="font-semibold">{{ t('subscription.iap.offerCode.label') }}</span>
        <Button
          :label="t('subscription.iap.offerCode.redeemButton')"
          icon="pi pi-tag"
          text
          size="small"
          :disabled="isAnyLoading || isIAPProcessing"
          :loading="isIAPProcessing"
          @click="redeemOfferCode"
        />
      </div>
      <p class="mt-1 text-xs text-gray-500">{{ t('subscription.iap.offerCode.description') }}</p>
    </div>

    <!-- Confirmation message for subscription changes -->
    <div v-if="isChangingSubscription && effectiveDowngradeValidation.canDowngrade" class="w-full rounded-lg bg-blue-50 p-4">
      <div class="text-sm text-blue-800">
        <p class="mb-2 font-semibold">{{ t('subscription.confirmChange.message') }}</p>
      </div>
    </div>

    <!-- Apple Subscription Legal Disclosure (Required for iOS In-App Purchases - Guideline 3.1.2) -->
    <div v-if="isNative && isIOS && !isChangingSubscription" class="w-full rounded-lg border border-gray-200 bg-gray-50 p-4 text-xs text-gray-600">
      <p class="mb-2">
        {{ t('subscription.iap.legalDisclosure.autoRenewalTerms') }}
      </p>
      <p class="mb-2">
        {{ t('subscription.iap.legalDisclosure.paymentTerms') }}
      </p>
      <p class="mb-3">
        {{ t('subscription.iap.legalDisclosure.managementTerms') }}
      </p>
      <div class="flex flex-wrap gap-4">
        <a
          href="#"
          class="text-primary-600 underline"
          @click.prevent="navigateToExternalUrl(`/conditions-generales/${appStore.language}`, { target: '_blank' })"
        >
          {{ t('subscription.iap.legalDisclosure.termsOfUse') }}
        </a>
        <a
          href="#"
          class="text-primary-600 underline"
          @click.prevent="navigateToExternalUrl('https://gastronomia.ai/privacy-policy', { target: '_blank' })"
        >
          {{ t('subscription.iap.legalDisclosure.privacyPolicy') }}
        </a>
      </div>
    </div>

    <!-- Action buttons with loading states -->
    <div class="mt-4 flex gap-2">
      <Button
        :label="isChangingSubscription ? t('subscription.confirmChange.confirm') : t('subscription.confirmSubscription.confirm')"
        :disabled="isConfirmDisabled"
        :loading="isSubmitting"
        @click="confirmAction"
      />
      <Button :label="t('common.cancel')" outlined :disabled="isSubmitting" @click="router.go(-1)" />
    </div>

    <!-- Address Dialog -->
    <Dialog
      v-model:visible="showAddressDialog"
      modal
      :header="t('profile.profile.restaurantTab.historyAndData.restaurantAdress')"
      :style="{ width: '25rem', maxWidth: '90vw' }"
      :closable="!isSavingAddress"
    >
      <div class="flex flex-col gap-2">
        <InputText
          v-model="tempAddressLine1"
          class="w-full"
          :placeholder="t('profile.profile.restaurantTab.historyAndData.addressPlaceholder')"
          :disabled="isSavingAddress"
        />
        <div class="flex gap-2">
          <InputText
            v-model="tempAddressPostalCode"
            type="number"
            class="w-1/3"
            :placeholder="t('profile.profile.restaurantTab.historyAndData.postalCodePlaceholder')"
            :disabled="isSavingAddress"
          />
          <InputText
            v-model="tempAddressCity"
            class="w-2/3"
            :placeholder="t('profile.profile.restaurantTab.historyAndData.cityPlaceholder')"
            :disabled="isSavingAddress"
          />
        </div>
      </div>
      <template #footer>
        <Button :label="t('common.cancel')" outlined :disabled="isSavingAddress" @click="cancelAddressDialog" />
        <Button :label="t('common.validate')" :loading="isSavingAddress" @click="validateAddressDialog" />
      </template>
    </Dialog>

    <!-- Force Downgrade Confirmation Dialog -->
    <Dialog
      v-model:visible="showForceDowngradeDialog"
      modal
      :header="t('subscription.downgrade.forceDowngrade.title')"
      :style="{ width: '28rem', maxWidth: '90vw' }"
      :closable="!isSubmitting"
    >
      <div class="flex flex-col items-center gap-4">
        <i class="pi pi-exclamation-triangle mt-1 text-4xl text-orange-500"></i>
        <div class="flex items-start gap-3">
          <div class="flex-1">
            <p class="mb-3">{{ t('subscription.downgrade.forceDowngrade.confirmMessage') }}</p>

            <div
              v-if="effectiveDowngradeValidation.impact.employees_to_remove && effectiveDowngradeValidation.impact.employees_to_remove > 0"
              class="mb-2 rounded border border-red-200 bg-red-50 p-3"
            >
              <div class="flex items-center gap-2 text-red-800">
                <i class="pi pi-user-minus"></i>
                <span class="font-medium">{{
                  t('subscription.downgrade.forceDowngrade.employeesWillBeRemoved', {
                    count: effectiveDowngradeValidation.impact.employees_to_remove,
                  })
                }}</span>
              </div>
            </div>

            <div
              v-if="effectiveDowngradeValidation.impact.admins_to_convert && effectiveDowngradeValidation.impact.admins_to_convert > 0"
              class="mb-2 rounded border border-orange-200 bg-orange-50 p-3"
            >
              <div class="flex items-center gap-2 text-orange-800">
                <i class="pi pi-arrow-down"></i>
                <span class="font-medium">{{
                  t('subscription.downgrade.forceDowngrade.adminsWillBeConverted', {
                    count: effectiveDowngradeValidation.impact.admins_to_convert,
                  })
                }}</span>
              </div>
            </div>

            <p class="mt-3 text-sm text-gray-600">{{ t('subscription.downgrade.forceDowngrade.cannotBeUndone') }}</p>
          </div>
        </div>
      </div>

      <template #footer>
        <Button :label="t('common.cancel')" outlined :disabled="isSubmitting" @click="cancelForceDowngrade" />
        <Button
          :label="t('subscription.downgrade.forceDowngrade.confirm')"
          severity="danger"
          :loading="isSubmitting"
          @click="confirmForceDowngrade"
        />
      </template>
    </Dialog>
  </div>
</template>
